import { useState, useEffect } from 'react';
import useEnhancedEffect from '@lmig/dotcom-aspect-hooks/useEnhancedEffect.ts';

const getValue = key => {
  try {
    const item = window.sessionStorage.getItem(key);

    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
};

/**
 * Allows state values to be persisted to sessionStorage
 * Check docs for useLocalStorage, they're 100% analogous
 *
 * @param {string} key Which key is used to store the value in sessionStorage
 * @param {*} initialValue Any JSON serializable data type
 */
const useSessionStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    const valueFromStorage = getValue(key);

    if (valueFromStorage) {
      return valueFromStorage;
    }

    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  // Since this can server render with the initial value, we want to be sure
  //   that when we've reached the client we update state with the latest value
  //   from the storage key.
  useEnhancedEffect(() => {
    const valueOnHydration = getValue(key);

    if (valueOnHydration) {
      setStoredValue(valueOnHydration);
    }
  }, []);

  // Keep the sessionStorage key in sync with the local state
  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(storedValue));
    } catch (e) { } // eslint-disable-line
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
};

export default useSessionStorage;
